import React, { useContext, useEffect, useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import FormControlLabel from '@mui/material/FormControlLabel';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import IOSSwitch from '../../Components/IOSSwitch';
import { TypoPoppins } from '../../Components/Typo';

import { COLORS } from '../../Constant/COLORS';

import { ORDER_STATUS } from '../../Constant/Enums';

import ToastContext from '../../Context/ToastContext';

import { toCapitalize, toLocalPhone } from '../../Helpers';

import { ProfessionalsQueryAdministrator, FindProfessional } from '../../Server/Query';
import { UpdateProfessionalActiveStatus } from '../../Server/Mutation';

import SearchField from './Components/SearchField';
import CustomTable from './Components/CustomTable';
import CommentsCard from './Components/CommentsCard';
import ConfirmationModal from './Components/ConfirmationModal';
import ServicePackageCard from './Components/ServicePackageCard';
import ProfessionalDetails from './Components/ProfessionalDetails';
import VerificationCard from './Components/VerificationCard';

const Professionals = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [isBlockConfirmationModalOpen, setIsBlockConfirmationModalOpen] = useState(false);
  const [tableRecords, setTableRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [isActionsDisabled, setIsActionsDisabled] = useState(true);
  const [selectedProfessionalDetails, setSelectedProfessionalDetails] = useState({
    id: '',
    name: '',
    postCode: '',
    address: '',
    province: '',
    phoneNumber: '',
    avatar: '',
    status: '',
    nicImage: '',
    addressProof: '',
    completedOrdersCount: 0,
    reviews: [],
    servicePackages: [],
    service: {
      name: '',
      location: '',
      serviceLink: '',
    },
  });

  const filteredTableRecords = useMemo(
    () =>
      search
        ? tableRecords.filter(professional => {
            const isProfessionalNameMatched = professional.name
              .toLowerCase()
              .startsWith(search.toLowerCase());

            const isProfessionalIdMatched = professional.professionalId
              .toLowerCase()
              .startsWith(search.toLowerCase());

            return isProfessionalNameMatched || isProfessionalIdMatched;
          })
        : tableRecords,
    [tableRecords, search]
  );

  useEffect(() => {
    Axios(ProfessionalsQueryAdministrator)
      .then(({ data, errors }) => {
        if (errors) {
          showToastMessage('Sorry, Failed to load professional details');
          return undefined;
        }

        const professionals = (data?.Professionals ?? []).map(createTableRecord).filter(Boolean);
        setTableRecords(professionals);
      })
      .catch(() => showToastMessage('Sorry, Failed to load professional details'));
  }, [showToastMessage]);

  function handleChangeSearch(event) {
    setSearch(event.target.value);
  }

  async function handleClickTableRecord(professionalId) {
    setIsActionsDisabled(false);

    // if user clicks same table record multiple time this prevent refetching
    const isSameRowClickedAgain = selectedProfessionalDetails.id === professionalId;
    if (isSameRowClickedAgain) return undefined;

    const query = FindProfessional({ professionalUniqueID: professionalId });
    const { data, errors } = await Axios(query);

    if (errors || data.Professional === null) {
      showToastMessage('Something went wrong');
      return undefined;
    }

    const professional = data.Professional;
    setIsBlocked(!getField(professional, 'isActive', 'is_active'));
    setSelectedProfessionalDetails(parseProfessional(professional));
  }

  function handleChangeToggle() {
    setIsBlockConfirmationModalOpen(true);
  }

  function handleCloseBlockConfirmationModal() {
    setIsBlockConfirmationModalOpen(false);
  }

  async function toggleProfessionalBlockedStatus() {
    try {
      const query = UpdateProfessionalActiveStatus({
        isActive: isBlocked,
        id: selectedProfessionalDetails.id,
      });
      const { errors } = await Axios(query);

      if (errors) {
        showToastMessage("For some reason, we couldn't update", 'error');
        return undefined;
      }

      setIsBlocked(currentState => !currentState);
      setIsBlockConfirmationModalOpen(false);
      showToastMessage('Updated successfully', 'success');
    } catch {
      // network/HTTP failure is already surfaced via the global API error toast
      setIsBlockConfirmationModalOpen(false);
    }
  }

  return (
    <>
      <Box my={{ xs: 2, md: 4 }} px={{ xs: 2.5, md: 5 }}>
        <Grid
          container
          columns={{ xs: 1, md: 2 }}
          columnSpacing={4}
          rowSpacing={{ xs: 2, md: 'unset' }}>
          <Grid item xs={1}>
            <Stack height={48} textAlign='right' mb={2} alignItems='flex-end' justifyContent='end'>
              <FormControlLabel
                sx={{ mr: { xs: 'initial', md: '-10px' } }}
                label={<TypoPoppins mr={2}>Block</TypoPoppins>}
                control={
                  <IOSSwitch
                    disableFocusRipple
                    disableRipple
                    checked={isBlocked}
                    onChange={handleChangeToggle}
                    disabled={isActionsDisabled}
                  />
                }
                labelPlacement='start'
              />
            </Stack>

            <ProfessionalDetails professionalDetails={selectedProfessionalDetails} px={3} py={4} />

            <Box mt={3}>
              <TypoPoppins variant='body1' variantMapping={{ h6: 'h2' }} mb={1}>
                Packages
              </TypoPoppins>
              <Stack maxHeight={500} overflow='auto' gap={2} sx={{ '& > *': { flex: '1 0 ' } }}>
                {selectedProfessionalDetails.servicePackages.map(servicePackage => (
                  <ServicePackageCard key={servicePackage.id} data={servicePackage} />
                ))}
              </Stack>
            </Box>

            <VerificationCard
              status={selectedProfessionalDetails.status}
              nicImage={selectedProfessionalDetails.nicImage}
              addressProof={selectedProfessionalDetails.addressProof}
            />
          </Grid>
          <Grid item xs={1}>
            <Stack
              mb={2}
              px={{ md: 4 }}
              direction='row'
              justifyContent={{ xs: 'center', md: 'unset' }}>
              <SearchField onChange={handleChangeSearch} value={search} />
            </Stack>
            <CustomTable
              px={3}
              tableHeadings={['No.', 'ID', 'Name', 'Email', 'Phone Number', 'Work', 'Status']}
              height='min(100%,500px)'>
              {filteredTableRecords.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  selected={row.id === selectedProfessionalDetails.id}
                  onClick={() => handleClickTableRecord(row.id)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>{row.no}</TableCell>
                  <TableCell>{row.professionalId}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{toLocalPhone(row.mobileNumber)}</TableCell>
                  <TableCell>{row.work}</TableCell>
                  <TableCell>
                    <StatusPill status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
            </CustomTable>
            <TypoPoppins my={3}>Comments : </TypoPoppins>

            <Stack
              gap={3}
              direction={{ xs: 'row', md: 'column' }}
              flexWrap={{ xs: 'wrap', md: 'initial' }}>
              {selectedProfessionalDetails.reviews.map(review => (
                <Box flexGrow={{ xs: '1', md: 'initial' }} key={review.orderId}>
                  <CommentsCard
                    date={review.date}
                    jobId={review.orderId}
                    review={review.review}
                    overallComments={review.overallComment}
                    name={review.name}
                  />
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <ConfirmationModal
        onClickConfirm={toggleProfessionalBlockedStatus}
        open={isBlockConfirmationModalOpen}
        onClose={handleCloseBlockConfirmationModal}
        content={{
          title: 'Block Confirmation',
          description: `Are you sure want to ${isBlocked ? 'unblock' : 'block'} this professional?`,
        }}
      />
    </>
  );
};

function StatusPill({ status }) {
  const map = {
    ACCEPTED: { color: COLORS.green4, bg: 'rgba(10,180,118,0.14)' },
    REQUESTED: { color: '#9a7a00', bg: 'rgba(235,208,67,0.22)' },
    REJECTED: { color: COLORS.red3, bg: 'rgba(255,33,33,0.10)' },
  };
  const style = map[String(status).toUpperCase()] || {
    color: COLORS.inkSoft,
    bg: COLORS.surfaceAlt,
  };
  return (
    <Box
      component='span'
      sx={{
        px: 1,
        py: 0.4,
        borderRadius: 999,
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: '0.72rem',
        whiteSpace: 'nowrap',
      }}>
      {status || '—'}
    </Box>
  );
}

StatusPill.propTypes = { status: PropTypes.string };

function createTableRecord(professional, index) {
  return {
    id: professional.id,
    no: (index + 1).toString().padStart(2, '0'),
    professionalId: getField(professional, 'professionalId', 'professional_id') ?? '',
    name: getFullName(professional),
    email: professional.email ?? '',
    mobileNumber: getField(professional, 'mobileNumber', 'mobile_number') ?? '',
    work: professional.Service?.name ? toCapitalize(professional.Service.name) : 'Not set',
    status: professional.status ? toCapitalize(professional.status) : '',
  };
}

function parseProfessional(professional) {
  const service = professional.Service;
  const servicePackages = service?.ServicePackages ?? [];

  const name = getFullName(professional);

  const data = {
    id: professional.id,
    name,
    postCode: getField(professional, 'postalCode', 'postal_code'),
    address: getField(professional, 'addressLine1', 'address_line_1'),
    province: professional.province,
    phoneNumber: getField(professional, 'mobileNumber', 'mobile_number'),
    avatar: professional.avatar,
    status: professional.status ?? '',
    nicImage: getField(professional, 'nicImage', 'nic_image') || '',
    addressProof: getField(professional, 'addressProof', 'address_proof') || '',
  };

  data.service = service ? parseService(service) : null;

  let completedOrderCount = 0;
  const reviews = servicePackages
    .flatMap(servicePackage => servicePackage.Orders)
    .map(order => {
      if (order.status === ORDER_STATUS.COMPLETED) completedOrderCount++;

      if (order.Review) {
        const review = order.Review;
        const { first_name, last_name } = order.EventBooker;
        const eventBookerName = first_name.concat(' ', last_name);
        return {
          name: eventBookerName,
          review: review.description,
          overallComment: getOverallComment(review.rating),
          orderId: order.id,
          date: review.created_at,
        };
      }

      return null;
    })
    .filter(Boolean);

  data.servicePackages = servicePackages.map(parseServicePackage);
  data.reviews = reviews;
  data.completedOrderCount = completedOrderCount;

  return data;
}

function parseService(service) {
  return {
    name: toCapitalize(service.name),
    location: service.location,
    serviceLink: `${service.website_url ?? ''} ${service.facebook_url ?? ''}`,
  };
}

function parseServicePackage(servicePackage) {
  return {
    id: servicePackage.id,
    servicePackageId: servicePackage.service_package_id,
    name: servicePackage.name,
    price: servicePackage.price,
    description: servicePackage.description,
  };
}

function getField(source, camelKey, snakeKey) {
  return source?.[camelKey] ?? source?.[snakeKey] ?? '';
}

function getFullName(person) {
  return [getField(person, 'firstName', 'first_name'), getField(person, 'lastName', 'last_name')]
    .filter(Boolean)
    .join(' ');
}

function getOverallComment(rating) {
  const overallComments = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  return overallComments[rating - 1];
}

export default Professionals;
